import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils";

/**
 * Logo Base Component
 *
 * Shared SVG wrapper providing consistent styling and accessibility
 * for all logo components in the design system.
 */

export const logoVariants = cva("shrink-0 w-auto", {
  variants: {
    size: {
      sm: "h-6",
      // 24px
      md: "h-10",
      // 40px
      lg: "h-16",
      // 64px
      xl: "h-20" // 80px
    }
  },
  defaultVariants: {
    size: "md"
  }
});
export interface LogoBaseProps extends React.SVGProps<SVGSVGElement>, VariantProps<typeof logoVariants> {
  /**
   * If true, the logo is purely decorative and will be hidden from screen readers
   * @default true
   */
  decorative?: boolean;
}
const LogoBase = React.forwardRef<SVGSVGElement, LogoBaseProps>((t0, ref) => {
  const $ = _c(19);
  let children;
  let className;
  let props;
  let size;
  let t1;
  let viewBox;
  if ($[0] !== t0) {
    ({
      size,
      decorative: t1,
      className,
      children,
      viewBox,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = viewBox;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    viewBox = $[6];
  }
  const decorative = t1 === undefined ? true : t1;
  let t2;
  if ($[7] !== decorative) {
    t2 = decorative ? {
      "aria-hidden": "true" as const
    } : {
      role: "img" as const
    };
    $[7] = decorative;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const ariaProps = t2;
  let t3;
  if ($[9] !== className || $[10] !== size) {
    t3 = cn(logoVariants({
      size
    }), className);
    $[9] = className;
    $[10] = size;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== ariaProps || $[13] !== children || $[14] !== props || $[15] !== ref || $[16] !== t3 || $[17] !== viewBox) {
    t4 = <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} fill="currentColor" className={t3} {...ariaProps} {...props}>{children}</svg>;
    $[12] = ariaProps;
    $[13] = children;
    $[14] = props;
    $[15] = ref;
    $[16] = t3;
    $[17] = viewBox;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
});
LogoBase.displayName = "LogoBase";
export { LogoBase };